/**
 * ETHOS Hold Shaper — central physical configuration.
 *
 * EVERY real-world dimension in the application comes from this file, in
 * millimetres. After ETHOS test-prints a hold, this is the only file that
 * should need editing to change screw geometry, foam sizes, base thickness
 * or export quality. Nothing else in the codebase hard-codes a millimetre.
 *
 * Axis convention (see docs in README):
 *   x, z  = horizontal footprint (the wall plane once mounted)
 *   y     = the mounting axis. y = 0 is the FLAT REAR FACE that contacts the
 *           climbing wall; +y points away from the wall, out of the workbench.
 * The STL writer converts to the Z-up convention slicers expect.
 */

export type BlockPresetId = "jib" | "small" | "medium" | "large";

export const APP_NAME = "ETHOS Hold Shaper V1.1";

export type FoamColorId = "pale-green" | "pale-sand" | "light-charcoal";

export interface FoamColorOption {
  readonly id: FoamColorId;
  readonly label: string;
  readonly hex: string;
}

export const FOAM_COLORS: readonly FoamColorOption[] = [
  { id: "pale-green", label: "Pale green", hex: "#c8d7bc" },
  { id: "pale-sand", label: "Pale sand", hex: "#d8c5a3" },
  { id: "light-charcoal", label: "Light charcoal", hex: "#747b77" },
] as const;

export const DEFAULT_FOAM_COLOR: FoamColorId = "pale-sand";

export function getFoamColor(id: FoamColorId): FoamColorOption {
  const found = FOAM_COLORS.find((option) => option.id === id);
  if (!found) throw new Error(`Unknown foam colour: ${id}`);
  return found;
}

export interface BlockPreset {
  readonly id: BlockPresetId;
  readonly label: string;
  /** Footprint width, mm (x). */
  readonly width: number;
  /** Footprint depth, mm (z). */
  readonly depth: number;
  /** Foam height above the mounting plane, mm (y). */
  readonly height: number;
  /**
   * Target sculpting voxel size in mm. Smaller = finer carving but more
   * memory and slower rebuilds. Chosen per preset so every block lands at a
   * comparable voxel budget (~1.0–1.4 M cells) and stays interactive.
   */
  readonly voxelSize: number;
  readonly blurb: string;
}

/**
 * Foam blanks offered on the setup screen. Adding a size is a one-line change
 * here — nothing else in the app enumerates presets.
 */
export const BLOCK_PRESETS: readonly BlockPreset[] = [
  {
    id: "jib",
    label: "Jib / Foothold",
    width: 50,
    depth: 50,
    height: 20,
    voxelSize: 0.5,
    blurb: "Great for footholds, jibs and tiny screw-ons.",
  },
  {
    id: "small",
    label: "Small",
    width: 150,
    depth: 150,
    height: 60,
    voxelSize: 1.25,
    blurb: "Crimps, edges and small pockets.",
  },
  {
    id: "medium",
    label: "Medium",
    width: 300,
    depth: 300,
    height: 100,
    voxelSize: 2.0,
    blurb: "Jugs, slopers and pinches.",
  },
  {
    id: "large",
    label: "Large",
    width: 600,
    depth: 600,
    height: 150,
    voxelSize: 3.6,
    blurb: "Big features and volumes.",
  },
] as const;

export function getPreset(id: BlockPresetId): BlockPreset {
  const found = BLOCK_PRESETS.find((p) => p.id === id);
  if (!found) throw new Error(`Unknown block preset: ${id}`);
  return found;
}

/**
 * Countersunk wood-screw fixing. Defaults suit a 5 mm / No.10 countersunk
 * screw through 18 mm ply, which is the usual ETHOS screw-on hold fixing.
 *
 * `countersinkAngle` is the INCLUDED angle of the screw head cone: 90 deg is
 * the common metric/imperial countersunk head. The cone depth is derived, so
 * changing the angle or either diameter stays self-consistent.
 */
export interface FixingSpec {
  readonly id: string;
  readonly label: string;
  readonly kind: "screw";
  /** Through-hole diameter, mm — clearance for the screw shank. */
  readonly shaftDiameter: number;
  /** Diameter at the mouth of the countersink, mm. */
  readonly countersinkDiameter: number;
  /** Included cone angle of the screw head, degrees. */
  readonly countersinkAngle: number;
  /**
   * How far below the local sculpted surface the countersink mouth is sunk,
   * mm. Positive values recess the screw head; 0 sits it flush.
   */
  readonly headClearance: number;
  /** Minimum material the fixing needs to pass through, mm. */
  readonly minColumnHeight: number;
  /** Radius kept clear of the hold edge when placing, mm. */
  readonly edgeMargin: number;
  /** Extra radial relief that softly blends the countersink into the surface. */
  readonly surfaceBlendRadius: number;
  /** Maximum angle the screw may lean away from the wall normal. */
  readonly maxTiltDegrees: number;
}

export const FIXING_TYPES: readonly FixingSpec[] = [
  {
    id: "screw-5mm",
    label: "5 mm countersunk wood screw",
    kind: "screw",
    shaftDiameter: 5.5,
    countersinkDiameter: 11.0,
    countersinkAngle: 90,
    headClearance: 1.0,
    minColumnHeight: 8,
    edgeMargin: 8,
    surfaceBlendRadius: 2.5,
    maxTiltDegrees: 38,
  },
] as const;

export const DEFAULT_FIXING_ID = "screw-5mm";

export function getFixingSpec(id: string): FixingSpec {
  const found = FIXING_TYPES.find((f) => f.id === id);
  if (!found) throw new Error(`Unknown fixing type: ${id}`);
  return found;
}

/** Sculpting behaviour and limits. */
export const SCULPT = {
  /**
   * Material that carving may never remove from directly above the mounting
   * plane, mm. Stops a downward stroke punching through the back of the hold.
   * Carving the SIDES of the block is unaffected — see `applyBrush`.
   */
  minBaseThickness: 6,
  /** Adjustable floor protection when the user enables Lock base. */
  minLockedBaseThickness: 1,
  maxLockedBaseThickness: 10,
  defaultLockedBaseThickness: 2,
  /** Default brush radius as a fraction of the block's largest footprint edge. */
  defaultRadiusFraction: 0.13,
  /** Hard brush radius limits, mm. */
  minRadius: 4,
  maxRadius: 220,
  /** Brush strength is a fraction; this scales it to mm of erosion per stroke step. */
  strengthToMillimetres: 2.6,
  /** Carve keeps the previous 20% cap; smoothing has its own wider range. */
  minStrength: 0,
  maxStrength: 0.2,
  maxSmoothStrength: 0.5,
  defaultStrength: 0.2,
  defaultSmoothStrength: 0.2,
  /** Stroke resampling: consecutive dabs are never further apart than this
   *  fraction of the brush radius, which is what makes strokes continuous. */
  strokeSpacingFraction: 0.22,
  /** SDF values are clamped to +/- this many voxels to keep the field sane. */
  fieldClampVoxels: 3.5,
} as const;

/** Whole-piece smoothing. The UI is always 0–100; these tune the field pass. */
export const OVERALL_SMOOTH = {
  defaultStrength: 50,
  /** 100% deliberately matches the old curve extrapolated to 200%. */
  maxPasses: 23,
  minBlend: 0.08,
  maxBlend: 0.76,
} as const;

/** Exported finish geometry and gloss-paint storage limits. */
export const FINISH = {
  gripAmplitudeMm: 0.28,
  maxGlossDabs: 512,
  previewGlossDabs: 96,
  dabSpacingFraction: 0.32,
} as const;

/** Additive floor flange dimensions, in millimetres. */
export const FLANGE = {
  minWidth: 2,
  maxWidth: 40,
  defaultWidth: 10,
  /** Flat material thickness at the outside edge of the ramp. */
  floorThickness: 2,
  /** Radius used to blend the additive ramp into the existing hold. */
  joinBlendRadius: 2.5,
} as const;

/** Final mesh-only polish. The SDF remains the source of truth. */
export const SURFACE_POLISH = {
  viewportIterations: 5,
  exportIterations: 7,
  fixingPreviewIterations: 5,
} as const;

/** Undo/redo. Snapshots are sub-volumes of the field, so budget by bytes. */
export const HISTORY = {
  maxEntries: 60,
  /** Total retained undo data before the oldest entries are dropped. */
  maxBytes: 96 * 1024 * 1024,
} as const;

/**
 * Export quality. V1 ships "standard"; the other two are wired through so a
 * quality selector is a UI change only.
 *
 * `voxelScale` multiplies the sculpting voxel size at export time. Values
 * below 1 resample the sculpted field onto a finer grid, which smooths the
 * isosurface without inventing detail.
 */
export type ExportQualityId = "draft" | "standard" | "fine";

export interface ExportQuality {
  readonly id: ExportQualityId;
  readonly label: string;
  readonly voxelScale: number;
  /** Radial segments used for the fixing-hole solids. */
  readonly fixingSegments: number;
}

export const EXPORT_QUALITIES: readonly ExportQuality[] = [
  { id: "draft", label: "Draft", voxelScale: 1.6, fixingSegments: 32 },
  { id: "standard", label: "Standard", voxelScale: 1.0, fixingSegments: 64 },
  { id: "fine", label: "Fine", voxelScale: 0.68, fixingSegments: 96 },
] as const;

export const DEFAULT_EXPORT_QUALITY: ExportQualityId = "standard";

export function getExportQuality(id: ExportQualityId): ExportQuality {
  const found = EXPORT_QUALITIES.find((q) => q.id === id);
  if (!found) throw new Error(`Unknown export quality: ${id}`);
  return found;
}

/** Viewport, camera and workbench presentation. */
export const VIEW = {
  /**
   * Camera is never allowed below this angle from vertical (degrees).
   * 90 deg is exactly level with the orbit target: you look along the mounting
   * plane and see the bench edge-on, which is what a front or side elevation
   * needs, but you can never get underneath it. Anything past 90 would let the
   * user look up at the mounting face.
   */
  maxPolarAngleDeg: 90,
  /** 0 gives a true plan view; OrbitControls keeps its own epsilon off the pole. */
  minPolarAngleDeg: 0,
  /** Zoom limits as multiples of the block's diagonal. */
  minDistanceFactor: 0.55,
  maxDistanceFactor: 3.4,
  /** How far the orbit target may be panned from the block centre, mm. */
  panLimitFactor: 0.55,
  /** Workbench overhang beyond the block footprint, mm. */
  benchMargin: 190,
  benchThickness: 14,
  /** Measurement grid pitch inside the glass, mm. */
  gridPitch: 50,
  /** How far back the orthographic standard views sit, in block diagonals. */
  standardViewDistanceFactor: 1.35,
  /** Fraction of the viewport the block fills in a fitted standard view. */
  standardViewFill: 0.78,
  /** Orthographic zoom limits, as multiples of the fitted zoom. */
  orthoZoomMin: 0.3,
  orthoZoomMax: 7,
  /** Seconds the camera takes to swing to a standard view. */
  viewTransitionSeconds: 0.45,
} as const;

/**
 * The Cut tool: draw a line across the hold and saw straight through it.
 *
 * The cut is a prismatic sweep of the drawn line along the view direction, so
 * it only makes sense from a parallel projection — selecting Cut puts the
 * camera into one of the orthographic standard views for exactly that reason.
 */
export const CUT = {
  /** The Angle tool constrains its straight guide to this many degrees. */
  angleStepDegrees: 5,
  /**
   * Half-thickness of the saw kerf, in voxels. Must stay above 0.5 so the
   * removed slab is thicker than one voxel and is therefore guaranteed to
   * separate the grid into genuinely disconnected pieces.
   */
  kerfVoxels: 0.9,
  /** Kerf floor in mm, so a fine grid still cuts a believable slot. */
  minKerfMm: 0.6,
  /** The drawn line is extended past both ends by this many block diagonals,
   *  which is what makes every cut a clean break rather than a partial slot. */
  extendFactor: 4,
  /** Minimum spacing between captured points, in NDC units. */
  minPointSpacingNdc: 0.006,
  maxPoints: 240,
  /** Resolution of the 2D distance grid used to evaluate the cut, in voxels. */
  distanceGridVoxels: 0.5,
} as const;

/** Local autosave. */
export const PERSISTENCE = {
  databaseName: "ethos-hold-designer",
  storeName: "designs",
  recordKey: "current",
  /** Quiet period after the last edit before the design is written, ms. */
  debounceMs: 1200,
  schemaVersion: 1,
} as const;

/** Smallest viewport the modelling workspace is offered on, px. */
export const MIN_DESKTOP_WIDTH = 900;
